import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { Tooltip } from "@/components/common/Tooltip";
import { buttonClasses } from "@/components/design/button-classes";
import { FilterChip } from "@/components/design/FilterChip";
import { PowerTile } from "@/components/design/PowerTile";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { PowersDataDocument } from "@/graphql/generated/operations";
import { ELEMENT_CODES } from "@/lib/elements";
import { rankLabel } from "@/lib/grow-rank";
import { POWER_GROUPS, POWER_LIBRARY, TAG_LABEL_IDS } from "@/lib/power-library";
import type { PowerTag } from "@/lib/power-library";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { useAgentStore } from "@/stores/agent-store";

// Grow · Power Library (workshop snapshot screens/PowerLibrary.jsx).
// All 30 powers grouped periodic-table-style; searchable by name/abbr.
// Scores come from the user's REAL data (PowersData) - switchable between
// Selbstbewertung (selfAssessmentResults) and Smart Analyse
// (smartAnalysisResults); the static `level` mocks in power-library.ts are
// deliberately NOT used here.

type ScoreSource = "self" | "smart" | "combined";

export function PowerLibraryPage() {
  const intl = useIntl();
  const ask = useAgentStore((s) => s.ask);
  const [query, setQuery] = useState("");
  // Which evaluation the tiles display - applies to ALL tiles at once.
  const [scoreSource, setScoreSource] = useState<ScoreSource>("smart");
  // Show only powers whose self-assessment is not yet complete, so the user
  // sees at a glance what still needs rating. Pre-activated via ?unrated=1
  // (e.g. the home dashboard's "Unbewertete anzeigen" quick jump).
  const [searchParams] = useSearchParams();
  const [onlyUnrated, setOnlyUnrated] = useState(searchParams.get("unrated") === "1");
  const { data } = useQuery(PowersDataDocument);

  // Both result sets are keyed by CreativityElement; translate to the
  // 3-letter power code so the library tiles can look them up.
  const sa = data?.selfAssessmentResults;
  const selfByAbbr = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const r of sa ?? []) {
      const code = ELEMENT_CODES[String(r.element)];
      if (code) m.set(code, r.result);
    }
    return m;
  }, [sa]);
  const sm = data?.smartAnalysisResults;
  const smartByAbbr = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const r of sm ?? []) {
      const code = ELEMENT_CODES[String(r.element)];
      if (code) m.set(code, r.score);
    }
    return m;
  }, [sm]);
  // Combined ("gewichtet") = mean of the available per-type scores (self + smart)
  // per power, mirroring genesis' fetchLatestAveragedScores (latest per type,
  // averaged across types). Computed client-side from the two values PowersData
  // already loads, so no extra backend call is needed.
  const combinedByAbbr = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    const codes = new Set<string>([...selfByAbbr.keys(), ...smartByAbbr.keys()]);
    for (const code of codes) {
      const vals = [selfByAbbr.get(code), smartByAbbr.get(code)].filter(
        (v): v is number => typeof v === "number",
      );
      m.set(code, vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null);
    }
    return m;
  }, [selfByAbbr, smartByAbbr]);

  const levelByAbbr =
    scoreSource === "self" ? selfByAbbr : scoreSource === "smart" ? smartByAbbr : combinedByAbbr;

  // Badge (medal) rule mirrors discovery: shown when the self-assessment for
  // that power is fully answered (answeredQuestionCount === questionCount).
  // Independent of the score-source toggle.
  const completedByAbbr = useMemo(() => {
    const s = new Set<string>();
    for (const r of sa ?? []) {
      const code = ELEMENT_CODES[String(r.element)];
      if (code && r.questionCount > 0 && r.answeredQuestionCount === r.questionCount) s.add(code);
    }
    return s;
  }, [sa]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return POWER_LIBRARY.filter((p) => {
      if (onlyUnrated && completedByAbbr.has(p.abbr)) return false;
      if (!q) return true;
      return (
        intl.formatMessage({ id: p.nameId }).toLowerCase().includes(q) ||
        p.abbr.toLowerCase().includes(q)
      );
    });
  }, [query, onlyUnrated, completedByAbbr, intl]);

  // Global grow score: share of all self-assessment questions answered across
  // every element (genesis GrowRankingController), paired with the earned rank.
  const ranking = data?.growRanking;

  // Live assistant context (motive 1, ADR 0020). NOTE ADR 0018: the assistant is integrated with
  // SELF-ASSESSMENT scores ONLY — never surface the smart/combined values here (that would defeat
  // the powerstats+GrowthAdvisor evaluation), regardless of which source the user is viewing.
  const selfScoreLine = useMemo(() => {
    if (!data) return null;
    return POWER_LIBRARY.map((p) => {
      const v = selfByAbbr.get(p.abbr);
      return `${intl.formatMessage({ id: p.nameId })} ${typeof v === "number" ? v : "unrated"}`;
    }).join("; ");
  }, [data, selfByAbbr, intl]);
  useAssistantContext(
    data
      ? [
          {
            description: "Power Map view state",
            value:
              `overall grow score ${ranking?.score ?? "n/a"}%; ` +
              `${completedByAbbr.size}/${POWER_LIBRARY.length} powers fully self-assessed` +
              (onlyUnrated ? "; filtered to unrated only" : "") +
              (query ? `; search "${query}"` : ""),
          },
          {
            description: "the user's self-assessment power scores (0–100 or unrated; the only power scores available to you)",
            value: selfScoreLine ?? "",
          },
        ]
      : null,
  );

  // Guided tour (/tour): the Power Map's controls and the tile grid, self-assessment framing
  // only (ADR 0018 — never mention smart/combined values).
  useAssistantTour(
    data
      ? {
          label: intl.formatMessage({
            id: "grow.powerLibrary.tour.label",
            defaultMessage: "Grow Map",
          }),
          summary: intl.formatMessage(
            {
              id: "grow.powerLibrary.tour.summary",
              defaultMessage:
                "This is the Power Map: all {total} powers, periodic-table style. You have fully self-assessed {completed} of {total} so far.",
            },
            { completed: completedByAbbr.size, total: POWER_LIBRARY.length },
          ),
          stops: [
            {
              anchor: "grow.map.scoreSource",
              title: intl.formatMessage({
                id: "grow.powerLibrary.tour.scoreSource.title",
                defaultMessage: "Score source",
              }),
              description: intl.formatMessage({
                id: "grow.powerLibrary.tour.scoreSource.desc",
                defaultMessage:
                  "Switch which evaluation the numbers on all tiles show. Pick Self-assessment to see the scores from your own ratings.",
              }),
            },
            {
              anchor: "grow.map.filters",
              title: intl.formatMessage({
                id: "grow.powerLibrary.tour.filters.title",
                defaultMessage: "Search & filter",
              }),
              description: intl.formatMessage(
                {
                  id: "grow.powerLibrary.tour.filters.desc",
                  defaultMessage:
                    "Search powers by name or abbreviation, or tap Unrated to see only the {unrated} powers whose self-assessment you haven't completed yet.",
                },
                { unrated: POWER_LIBRARY.length - completedByAbbr.size },
              ),
            },
            {
              anchor: "grow.map.grid",
              title: intl.formatMessage({
                id: "grow.powerLibrary.tour.grid.title",
                defaultMessage: "The power tiles",
              }),
              description: intl.formatMessage(
                {
                  id: "grow.powerLibrary.tour.grid.desc",
                  defaultMessage:
                    "All {total} powers grouped into {clusters} clusters. Click a tile to open that power. A medal and its artwork appear once its self-assessment is complete; {completed} earned so far.",
                },
                {
                  total: POWER_LIBRARY.length,
                  clusters: POWER_GROUPS.length,
                  completed: completedByAbbr.size,
                },
              ),
            },
          ],
        }
      : null,
  );

  return (
    <div
      className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="PowersData"
    >
      <div className="mb-5 flex items-baseline justify-between gap-3">
        <BackButton />
      </div>

      <div className="flex items-end justify-between gap-6">
        <div className="min-w-0 flex-1">
          <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/45">
            <FormattedMessage id="grow.powerLibrary.eyebrow" defaultMessage="Power Map" />
          </div>
          <h1 className="font-serif text-5xl leading-tight">
            <FormattedMessage
              id="grow.powerLibrary.heading"
              defaultMessage="{count} Powers"
              values={{ count: POWER_LIBRARY.length }}
            />
          </h1>
          <p className="mt-3 max-w-xl text-sm text-ink/55">
            <FormattedMessage
              id="grow.powerLibrary.subheading"
              defaultMessage="The complete vocabulary: {clusters} clusters, {powers} powers."
              values={{ clusters: POWER_GROUPS.length, powers: POWER_LIBRARY.length }}
            />
          </p>
          <button
            type="button"
            onClick={() => {
              // Randomize among 10 equivalent phrasings (grow.powerLibrary.askIntro.0..9) for variety.
              const v = Math.floor(Math.random() * 10);
              ask(intl.formatMessage({ id: `grow.powerLibrary.askIntro.${v}` }));
            }}
            className={buttonClasses({ variant: "primary", size: "compact", className: "mt-4" })}
          >
            <FormattedMessage id="grow.powerLibrary.askButton" />
          </button>

          {/* Lens — switches which evaluation ALL tiles display.
              Conceptually different from the filters below: this re-interprets
              the numbers, it doesn't reduce the set. Sits on its own row. */}
          <div
            className="mt-8 flex flex-wrap items-center gap-2"
            data-assistant-anchor="grow.map.scoreSource"
          >
            <span className="field-label text-ink/70">
              <FormattedMessage id="grow.powerLibrary.powerScore" defaultMessage="Power Score" />
            </span>
            <SegmentedControl<ScoreSource>
              value={scoreSource}
              onChange={setScoreSource}
              options={[
                {
                  value: "smart",
                  label: intl.formatMessage({
                    id: "grow.powerLibrary.sourceSmart",
                    defaultMessage: "Smart Analysis",
                  }),
                },
                {
                  value: "self",
                  label: intl.formatMessage({
                    id: "grow.powerLibrary.sourceSelf",
                    defaultMessage: "Self-assessment",
                  }),
                },
                {
                  value: "combined",
                  label: intl.formatMessage({
                    id: "grow.powerLibrary.sourceCombined",
                    defaultMessage: "Combined",
                  }),
                },
              ]}
            />
          </div>

          {/* Filter row — search + status chips both narrow the visible set. */}
          <div
            className="mt-4 flex flex-wrap items-center gap-3"
            data-assistant-anchor="grow.map.filters"
          >
            <div className="flex w-full max-w-[14rem] items-center gap-2 rounded-full bg-transparent px-4 py-1.5 ring-1 ring-ink/15 transition focus-within:ring-ink/30">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "grow.powerLibrary.searchPlaceholder",
                  defaultMessage: "Search by name or abbreviation",
                })}
                className="flex-1 bg-transparent text-[12px] text-ink placeholder:text-ink/45 focus:outline-none"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label={intl.formatMessage({
                    id: "grow.powerLibrary.clearSearch",
                    defaultMessage: "Clear search",
                  })}
                  className="shrink-0 text-sm text-ink/40 hover:text-ink"
                >
                  ✕
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <FilterChip active={!onlyUnrated} onClick={() => setOnlyUnrated(false)}>
                <FormattedMessage id="grow.powerLibrary.all" defaultMessage="All" />
              </FilterChip>
              <FilterChip active={onlyUnrated} onClick={() => setOnlyUnrated(true)}>
                <FormattedMessage id="grow.powerLibrary.onlyUnrated" defaultMessage="Unrated" />
              </FilterChip>
            </div>
          </div>
        </div>
        {ranking ? (
          <Tooltip
            className="shrink-0"
            placement="left"
            content={intl.formatMessage({
              id: "grow.powerLibrary.profileProgressTooltip",
              defaultMessage:
                "Answer all questions about the powers to increase your profile progress.",
            })}
          >
            <div className="box-flat cursor-help whitespace-nowrap bg-surface px-5 py-3 text-center">
              <div className="field-label text-ink/55">
                <FormattedMessage
                  id="grow.powerLibrary.profileProgress"
                  defaultMessage="Profile progress"
                />
              </div>
              <div className="mt-1 font-serif text-4xl leading-none tabular-nums">{ranking.score}&thinsp;%</div>
              <div className="mt-1 text-[11px] text-ink/55">{rankLabel(ranking.userRank, intl.locale)}</div>
            </div>
          </Tooltip>
        ) : null}
      </div>

      {/* Periodic-table layout: each of the six genesis clusters sits in its
          own framed block (3 per row on desktop), in the stable POWER_GROUPS
          order. Search acts inside the blocks; empty blocks disappear. */}
      <div
        className="mt-6 grid grid-cols-1 items-start gap-3 @sm:grid-cols-2 @lg:grid-cols-3"
        data-assistant-anchor="grow.map.grid"
      >
        {POWER_GROUPS.map((t) => (
          <ClusterBlock
            key={t}
            tag={t}
            entries={filtered.filter((p) => p.tag === t)}
            levelByAbbr={levelByAbbr}
            badgeByAbbr={completedByAbbr}
          />
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-12 text-center text-ink/55">
          <FormattedMessage
            id="grow.powerLibrary.noResults"
            defaultMessage="No power found for “{query}”."
            values={{ query }}
          />
        </div>
      ) : null}
    </div>
  );
}

// One framed cluster block of the "periodic table": header with the cluster
// letter (element-symbol style) + name + count, a faint watermark letter, and
// the power tiles inside.
function ClusterBlock({
  tag,
  entries,
  levelByAbbr,
  badgeByAbbr,
}: {
  tag: PowerTag;
  entries: readonly (typeof POWER_LIBRARY)[number][];
  levelByAbbr: ReadonlyMap<string, number | null | undefined>;
  badgeByAbbr: ReadonlySet<string>;
}) {
  const navigate = useNavigate();
  const intl = useIntl();
  if (entries.length === 0) return null;
  return (
    <section className="box-flat relative overflow-hidden bg-surface p-3">
      <div className="mb-2 flex items-baseline gap-2">
        <span className="field-meta font-medium">{intl.formatMessage({ id: TAG_LABEL_IDS[tag] })}</span>
        <span className="field-label text-ink/55">{entries.length}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 @sm:grid-cols-4">
        {entries.map((p) => {
          const level = levelByAbbr.get(p.abbr);
          const isMeasured = typeof level === "number";
          // Artwork unlocks (hover reveal) only once the self-assessment is
          // complete - same gate as the medal.
          const rated = badgeByAbbr.has(p.abbr);
          return (
            <PowerTile
              key={p.abbr}
              tag={p.tag}
              abbr={p.abbr}
              name={intl.formatMessage({ id: p.nameId })}
              // Uniform card style regardless of source/measured - only the
              // number appears/changes when toggling the score source.
              active={false}
              score={isMeasured ? level : null}
              // Medal when the self-assessment for this power is complete.
              badge={rated}
              imageSrc={rated ? `/powers/${p.abbr.toLowerCase()}.jpg` : null}
              onSelect={() => navigate(`/grow/power/${p.abbr}`)}
              selectTitle={intl.formatMessage(
                { id: "grow.powerLibrary.openPower", defaultMessage: "Open {name}" },
                { name: intl.formatMessage({ id: p.nameId }) },
              )}
            />
          );
        })}
      </div>
      {/* Faint element-symbol watermark in the top-right corner. Sized
          (text-2xl) to fit within the clean header band above the tile grid:
          the band is ~28px tall (p-3 top + field-meta line + mb-2), so a
          larger text-4xl mark spilled onto the first tile row, where artwork on
          a rated power swallowed its lower half. At this size the full
          two-letter mark stays on clean surface. pointer-events-none keeps
          clicks passing through. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-2 top-2 z-20 select-none font-serif text-2xl leading-none text-ink/[0.12]"
      >
        {tag}
      </span>
    </section>
  );
}
